import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from './Icon';

type Variant = 'default' | 'primary' | 'ghost' | 'alert';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm' | 'md';
  icon?: IconName;
  iconRight?: IconName;
  loading?: boolean;
  children?: ReactNode;
};

/**
 * Direct port of the MAFA `.btn` class system. Variants are mutually
 * exclusive; `loading` disables the button and swaps the leading icon for
 * a spinner. Use the forwarded ref when attaching keyboard handlers or
 * focus management.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', icon, iconRight, loading, children, className, disabled, ...rest },
  ref,
) {
  const classes = ['btn'];
  if (variant === 'primary') classes.push('primary');
  if (variant === 'ghost') classes.push('ghost');
  if (variant === 'alert') classes.push('alert');
  if (size === 'sm') classes.push('sm');
  if (className) classes.push(className);

  return (
    <button ref={ref} className={classes.join(' ')} disabled={loading || disabled} {...rest}>
      {loading ? (
        <span className={variant === 'primary' || variant === 'alert' ? 'spin dark' : 'spin'} />
      ) : (
        icon && <Icon name={icon} />
      )}
      {children}
      {iconRight && !loading && <Icon name={iconRight} />}
    </button>
  );
});
